import { Component, computed, effect, inject, input, signal } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { NavigationComponent } from "../../components/common/navigation/navigation";
import { CertTabs } from "../../components/cert/cert-tabs/cert-tabs";
import { CertHeaderComponent } from "../../components/cert/cert-header/cert-header";
import { DdosTabComponent } from "./components/ddos-tab/ddos-tab";
import { ScanTab } from "./components/scan-tab/scan-tab";
import { FooterComponent } from "../../components/common/footer/footer";
import { DnsFirewallTabComponent } from "./components/dns-firewall-tab/dns-firewall-tab";
import { AIRT_SCANS_KEY, DDOS_FILTER_KEY, DNS_FIREWALL_KEY } from "../../components/cert/cert-tabs/cert-tabs.constants";
import { Apollo, ApolloBase } from "apollo-angular";
import { LIST_QUERIES } from "../../graphql/replication/subscription-list";
import { AuthService } from "../../services/AuthService";
import { PrefixSubscriptionFilter } from "./types";
import { PrivateQuery } from "../../gql/generated-ingestor";
import { ProductType } from "../../helpers/enums/productType";

@Component({
  selector: "page-cert",
  templateUrl: "cert.html",
  styleUrls: ["cert.scss"],
  imports: [
    CertHeaderComponent,
    CertTabs,
    DdosTabComponent,
    DdosTabComponent,
    DnsFirewallTabComponent,
    FooterComponent,
    NavigationComponent,
    ScanTab,
    TranslateModule,
  ],
})
export class CertPage {
  private router = inject(Router);
  private apollo = inject(Apollo);
  private auth = inject(AuthService);
  private replication: ApolloBase;

  // this value is read from the URL (see dashboard-routing.module.ts)
  // Setting a default string does not work, because if `:initialTab` is absent in the URL,
  // the signal defaults to undefined
  readonly initialTab = input<string | undefined>(undefined);
  protected activeTab = signal(AIRT_SCANS_KEY);

  protected showAirtScansTab = computed(() => this.activeTab() === AIRT_SCANS_KEY);
  protected showDdosFilterTab = computed(() => this.activeTab() === DDOS_FILTER_KEY);
  protected showDnsFirewallTab = computed(() => this.activeTab() === DNS_FIREWALL_KEY);

  public subscriptions = signal<PrefixSubscriptionFilter[]>([]);
  public prefixes = signal<string[]>([]);

  constructor() {
    this.replication = this.apollo.use("replicatedData");

    effect(() => {
      const init = this.initialTab();
      const validTab = [AIRT_SCANS_KEY, DDOS_FILTER_KEY, DNS_FIREWALL_KEY].includes(init?.toLowerCase());
      this.activeTab.set(validTab ? init : AIRT_SCANS_KEY);
      this.fetchPrefixSubscriptions();
    });
  }

  changeTab(tab: string) {
    this.router.navigate(["/cert", tab], { replaceUrl: true });
    this.activeTab.set(tab);
  }

  fetchPrefixSubscriptions() {
    this.replication
      .query<Pick<PrivateQuery, "ipPrefixList">>({
        query: LIST_QUERIES[ProductType.IPPrefixes],
        errorPolicy: "all",
        variables: {
          customer: this.auth.state.viewingCustomer.customerId,
        },
      })
      .subscribe(({ data }) => {
        const subscriptions = data.ipPrefixList.page.map((prefixSubscription) => ({
          id: prefixSubscription.subscriptionId,
          prefix: prefixSubscription.ipPrefix?.prefix,
        }));
        this.subscriptions.set(subscriptions);
        this.prefixes.set(subscriptions.map((subscription) => subscription.prefix));
      });
  }
}
